import type { EditorHistoryCommand } from '@/editor/history/types'

/**
 * 双栈撤销 / 重做，支持最大长度与丢弃时 `dispose`。
 */
export class UndoRedoStack {
  private undoStack: EditorHistoryCommand[] = []

  private redoStack: EditorHistoryCommand[] = []

  private readonly getMaxUndoDepth: () => number

  /**
   * @param getMaxUndoDepth - 最大可撤销步数（不含当前）
   */
  constructor(getMaxUndoDepth: () => number) {
    this.getMaxUndoDepth = getMaxUndoDepth
  }

  /**
   * @returns 是否还能撤销
   */
  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  /**
   * @returns 是否还能重做
   */
  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  /**
   * 新操作已应用到文档后调用：清空重做栈并入撤销栈。
   *
   * @param cmd - 已生效操作对应的命令
   */
  push(cmd: EditorHistoryCommand): void {
    this.redoStack = []
    this.undoStack.push(cmd)
    this.trimUndoFromFront()
  }

  /**
   * 撤销一步。
   *
   * @returns 是否成功
   */
  undo(): boolean {
    const cmd = this.undoStack.pop()
    if (!cmd) return false
    cmd.revert()
    this.redoStack.push(cmd)
    return true
  }

  /**
   * 重做一步。
   *
   * @returns 是否成功
   */
  redo(): boolean {
    const cmd = this.redoStack.pop()
    if (!cmd) return false
    cmd.redo()
    this.undoStack.push(cmd)
    return true
  }

  /**
   * 清空双栈（工程加载 / 重置时）。
   */
  clear(): void {
    for (const c of this.undoStack) {
      c.dispose?.('cleared')
    }
    for (const c of this.redoStack) {
      c.dispose?.('cleared')
    }
    this.undoStack = []
    this.redoStack = []
  }

  /**
   * 丢弃最旧的撤销记录直至满足容量（从栈底移除）。
   */
  private trimUndoFromFront(): void {
    const max = Math.max(1, Math.floor(this.getMaxUndoDepth()))
    while (this.undoStack.length > max) {
      const evicted = this.undoStack.shift()
      evicted?.dispose?.('evicted')
    }
  }
}
